class FormStorageService {
    STORAGE_PREFIX = 'EVENT_FORM_DATA_';
    SESSION_PREFIX = 'EVENT_SESSION_';
    /**
     * Generate a unique storage key for a specific event
     */
    getEventStorageKey(eventId) {
        return `${this.STORAGE_PREFIX}${eventId}`;
    }
    /**
     * Generate a unique session key for a specific event
     */
    getEventSessionKey(eventId) {
        return `${this.SESSION_PREFIX}${eventId}`;
    }
    /**
     * Save form field data for a specific event
     */
    saveFormField(eventId, fieldId, value) {
        try {
            const storageKey = this.getEventStorageKey(eventId);
            const existingData = this.getFormData(eventId);
            // Update or add the field data
            const updatedFields = existingData.fields.filter((field) => field.fieldId !== fieldId);
            updatedFields.push({
                fieldId,
                value,
                timestamp: Date.now()
            });
            const formData = {
                eventId,
                fields: updatedFields,
                lastUpdated: Date.now()
            };
            localStorage.setItem(storageKey, JSON.stringify(formData));
        }
        catch (error) {
            console.error('Error saving form field data:', error);
        }
    }
    /**
     * Get all form data for a specific event
     */
    getFormData(eventId) {
        try {
            const storageKey = this.getEventStorageKey(eventId);
            const storedData = localStorage.getItem(storageKey);
            if (storedData) {
                return JSON.parse(storedData);
            }
        }
        catch (error) {
            console.error('Error retrieving form data:', error);
        }
        return {
            eventId,
            fields: [],
            lastUpdated: Date.now()
        };
    }
    /**
     * Get a specific field value for an event
     */
    getFieldValue(eventId, fieldId) {
        const formData = this.getFormData(eventId);
        const field = formData.fields.find((f) => f.fieldId === fieldId);
        return field ? field.value : undefined;
    }
    /**
     * Get all field values for an event as a key-value object
     */
    getFieldValues(eventId) {
        const formData = this.getFormData(eventId);
        const values = {};
        formData.fields.forEach((field) => {
            values[field.fieldId] = field.value;
        });
        return values;
    }
    /**
     * Clear all form data for a specific event
     */
    clearFormData(eventId) {
        try {
            const storageKey = this.getEventStorageKey(eventId);
            localStorage.removeItem(storageKey);
        }
        catch (error) {
            console.error('Error clearing form data:', error);
        }
    }
    /**
     * Clear a specific field for an event
     */
    clearField(eventId, fieldId) {
        try {
            const formData = this.getFormData(eventId);
            const updatedFields = formData.fields.filter((field) => field.fieldId !== fieldId);
            const updatedFormData = {
                ...formData,
                fields: updatedFields,
                lastUpdated: Date.now()
            };
            const storageKey = this.getEventStorageKey(eventId);
            localStorage.setItem(storageKey, JSON.stringify(updatedFormData));
        }
        catch (error) {
            console.error('Error clearing field data:', error);
        }
    }
    /**
     * Check if form data exists for an event
     */
    hasFormData(eventId) {
        const formData = this.getFormData(eventId);
        return formData.fields.length > 0;
    }
    /**
     * Get the last updated timestamp for an event's form data
     */
    getLastUpdated(eventId) {
        const formData = this.getFormData(eventId);
        return formData.lastUpdated;
    }
    /**
     * Clean up old form data (older than specified days)
     */
    cleanupOldData(daysOld = 7) {
        try {
            const cutoffTime = Date.now() - daysOld * 24 * 60 * 60 * 1000;
            Object.keys(localStorage).forEach((key) => {
                if (key.startsWith(this.STORAGE_PREFIX)) {
                    const data = localStorage.getItem(key);
                    if (data) {
                        try {
                            const formData = JSON.parse(data);
                            if (formData.lastUpdated < cutoffTime) {
                                localStorage.removeItem(key);
                            }
                        }
                        catch (error) {
                            // If data is corrupted, remove it
                            localStorage.removeItem(key);
                        }
                    }
                }
            });
        }
        catch (error) {
            console.error('Error cleaning up old form data:', error);
        }
    }
    /**
     * Get all events that have stored form data
     */
    getEventsWithData() {
        const events = [];
        try {
            Object.keys(localStorage).forEach((key) => {
                if (key.startsWith(this.STORAGE_PREFIX)) {
                    const eventId = key.replace(this.STORAGE_PREFIX, '');
                    if (this.hasFormData(eventId)) {
                        events.push(eventId);
                    }
                }
            });
        }
        catch (error) {
            console.error('Error getting events with data:', error);
        }
        return events;
    }
}
export default new FormStorageService();
